import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import {
  ArrowLeft, Plus, UploadCloud, Download, Share2, Trash2,
  ChevronUp, ChevronDown, AlertTriangle, Bold, Italic, Underline, Strikethrough,
  Loader2, Check, AlertCircle, Baseline, Highlighter, Ban, GripVertical, History, Save,
  List, ListOrdered, Lock, Unlock,
} from "lucide-react";
import { SheetsProvider, useSheets } from "./modal/SheetsContext";
import CsvImportModal from "./ui/CsvImportModal";
import ShareSheetModal from "./ui/ShareSheetModal";
import VersionHistoryModal from "./ui/VersionHistoryModal";
import type { SheetColumn, SheetColumnType, SheetRow } from "./api/SheetsApi";
import DOMPurify from "dompurify";
import { formatSheetDate } from "./sheetDate";

const COLUMN_TYPE_OPTIONS: { value: SheetColumnType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Yes / No" },
  { value: "url", label: "URL" },
  { value: "select", label: "Dropdown" },
];

// Small enough that a resize handle and a row's action icons stay usable,
// but the row never visually collapses to nothing.
const MIN_ROW_HEIGHT = 28;

const SAFE_URL_RE = /^https?:\/\//i;
function isSafeUrl(value: string): boolean {
  return SAFE_URL_RE.test(value.trim());
}

// Spreadsheet-style column naming: 0 -> A, 25 -> Z, 26 -> AA, 27 -> AB, ...
function indexToSpreadsheetLabel(n: number): string {
  let s = "";
  let i = n + 1;
  while (i > 0) {
    const rem = (i - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    i = Math.floor((i - 1) / 26);
  }
  return s;
}

// "+ Column" adds one immediately with a default label the user can rename
// inline right away, rather than making them fill out a form first — this
// continues the same A, B, C... scheme the sheet started with, skipping any
// letter already in use so a quick-add never collides with an existing key.
function nextDefaultColumn(existing: SheetColumn[]): { key: string; label: string } {
  const usedKeys = new Set(existing.map((c) => c.key));
  let n = existing.length;
  while (true) {
    const label = indexToSpreadsheetLabel(n);
    const key = label.toLowerCase();
    if (!usedKeys.has(key)) return { key, label };
    n += 1;
  }
}

// Coerces one pasted clipboard cell's raw text into the value shape a column
// of that type expects — same idea as the single-cell commit() below, but
// standalone since a paste never goes through an EditableCell's own input.
function coerceValueForColumn(column: SheetColumn, raw: string): unknown {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  if (column.type === "number") {
    const n = Number(trimmed);
    return Number.isNaN(n) ? trimmed : n;
  }
  if (column.type === "boolean") {
    const lower = trimmed.toLowerCase();
    if (["true", "1", "yes", "y"].includes(lower)) return true;
    if (["false", "0", "no", "n"].includes(lower)) return false;
    // Unrecognized — fall back to the raw text rather than guessing (every
    // non-empty string is truthy in JS, so a naive Boolean(trimmed) here
    // would silently turn any unrecognized value into `true`).
    return trimmed;
  }
  return trimmed;
}

type NavDirection = "up" | "down" | "left" | "right";

// Always sanitizes first — callers include the sortingFn below, which runs
// on raw cell values that may never have passed through DOMPurify at all
// (CSV/XLSX import and paste don't sanitize text-column values; only a
// commit from the rich editor itself does). Without this, div.innerHTML =
// html on an unsanitized string is a live DOM-XSS sink — e.g. an <img
// onerror=...> fires its handler even on a detached, unattached element.
function stripHtml(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = DOMPurify.sanitize(html);
  return div.textContent || "";
}

// Walks up from a Selection's anchorNode (a Text node or an Element,
// depending on where the caret landed) to the nearest enclosing <li>, if any.
function closestListItem(node: Node | null): HTMLLIElement | null {
  const el = node instanceof Element ? node : node?.parentElement ?? null;
  return (el?.closest("li") as HTMLLIElement | null) ?? null;
}

// Shared by RichTextCellEditor's onPaste and handlePaste below — both split
// pasted clipboard text into lines and must treat line endings/a trailing
// blank line identically, since they're deciding between two different
// treatments of what would otherwise look like the same input. Splits on
// any of CRLF/CR/LF (not just LF) and drops one trailing empty line (from a
// paste that itself ends in a line break) so it doesn't show up as a spurious
// blank line/row.
function splitPasteLines(text: string): string[] {
  const lines = text.split(/\r\n|\r|\n/);
  while (lines.length && lines[lines.length - 1] === "") lines.pop();
  return lines;
}

// Wraps the current window selection in a sized span — used instead of
// document.execCommand("fontSize", ...), which only knows how to produce
// legacy <font size="N"> tags. No-ops when nothing is selected (there's
// nothing to wrap around a bare cursor position).
function applyFontSizeToSelection(size: "sm" | "base" | "lg") {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
  const range = sel.getRangeAt(0);

  // contentEditable doesn't trap selection — a mouse-drag can start inside
  // the active cell and extend into a neighboring (non-editable) cell's
  // React-rendered DOM. Bail unless the whole range is actually contained
  // in the currently-focused editable cell, or extractContents()/insertNode
  // below would mutate DOM React doesn't know about, corrupting a sibling
  // cell and likely crashing on its next re-render.
  const activeEditable = document.activeElement;
  if (!(activeEditable instanceof HTMLElement) || !activeEditable.isContentEditable) return;
  if (!activeEditable.contains(range.commonAncestorContainer)) return;

  const span = document.createElement("span");
  span.className = size === "sm" ? "text-xs" : size === "lg" ? "text-lg" : "text-sm";
  span.appendChild(range.extractContents());
  range.insertNode(span);

  // extractContents() can leave a now-empty size span behind when the
  // selection was fully inside one (e.g. resizing already-sized text) —
  // clean those up rather than letting them accumulate in the stored HTML.
  activeEditable.querySelectorAll("span.text-xs, span.text-sm, span.text-lg").forEach((el) => {
    if (!el.textContent) el.remove();
  });

  sel.removeAllRanges();
  const newRange = document.createRange();
  newRange.selectNodeContents(span);
  sel.addRange(newRange);
}

// Same wrap-the-selection-in-a-span approach as applyFontSizeToSelection
// above, generalized to an inline style property — execCommand("foreColor")
// and especially ("hiliteColor"/"backColor") are inconsistent across
// browsers (Safari has no hiliteColor support at all), so this stays fully
// custom rather than delegating to the browser.
function applyColorToSelection(prop: "color" | "backgroundColor", value: string) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
  const range = sel.getRangeAt(0);

  const activeEditable = document.activeElement;
  if (!(activeEditable instanceof HTMLElement) || !activeEditable.isContentEditable) return;
  if (!activeEditable.contains(range.commonAncestorContainer)) return;

  const span = document.createElement("span");
  span.style[prop] = value;
  span.appendChild(range.extractContents());
  range.insertNode(span);

  activeEditable.querySelectorAll("span[style]").forEach((el) => {
    if (!el.textContent) el.remove();
  });

  sel.removeAllRanges();
  const newRange = document.createRange();
  newRange.selectNodeContents(span);
  sel.addRange(newRange);
}

// The same 70-color grid Google Docs/Sheets/Slides all share: a grayscale
// row (black to white) followed by 6 rows of the 10 standard hues at
// progressively darker shades — shared by both the font-color and
// highlight-color pickers below, matching Sheets' own picker.
const STANDARD_PALETTE: string[] = [
  "#000000", "#434343", "#666666", "#999999", "#b7b7b7", "#cccccc", "#d9d9d9", "#efefef", "#f3f3f3", "#ffffff",
  "#980000", "#ff0000", "#ff9900", "#ffff00", "#00ff00", "#00ffff", "#4a86e8", "#0000ff", "#9900ff", "#ff00ff",
  "#e6b8af", "#f4cccc", "#fce5cd", "#fff2cc", "#d9ead3", "#d0e0e3", "#c9daf8", "#cfe2f3", "#d9d2e9", "#ead1dc",
  "#dd7e6b", "#ea9999", "#f9cb9c", "#ffe599", "#b6d7a8", "#a2c4c9", "#a4c2f4", "#9fc5e8", "#b4a7d6", "#d5a6bd",
  "#cc4125", "#e06666", "#f6b26b", "#ffd966", "#93c47d", "#76a5af", "#6d9eeb", "#6fa8dc", "#8e7cc3", "#c27ba0",
  "#a61c00", "#cc0000", "#e69138", "#f1c232", "#6aa84f", "#45818e", "#3c78d8", "#3d85c6", "#674ea7", "#a64d79",
  "#85200c", "#990000", "#b45f06", "#bf9000", "#38761d", "#134f5c", "#1155cc", "#0b5394", "#351c75", "#741b47",
];

function ColorSwatchButton({
  icon,
  onPick,
  disabled,
  title,
  resetLabel,
  resetValue,
}: {
  icon: React.ReactNode;
  onPick: (value: string) => void;
  disabled: boolean;
  title: string;
  resetLabel: string;
  resetValue: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        title={title}
        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      >
        {icon}
      </button>
      {open && (
        <div
          className="absolute z-20 top-full left-0 mt-1 bg-surface-muted border border-border rounded-xl shadow-2xl"
          style={{ padding: "10px" }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(10, 20px)", gap: "5px" }}>
            {STANDARD_PALETTE.map((hex) => (
              <button
                key={hex}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onPick(hex); setOpen(false); }}
                title={hex}
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "5px",
                  backgroundColor: hex,
                  boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.18)",
                }}
                className="appearance-none shrink-0 hover:scale-110 transition-transform"
              />
            ))}
          </div>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { onPick(resetValue); setOpen(false); }}
            className="mt-2 w-full flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-surface transition-colors font-inter"
          >
            <Ban className="w-3.5 h-3.5" />
            {resetLabel}
          </button>
        </div>
      )}
    </div>
  );
}

// Read-only / click-to-activate display of a text cell's rich HTML value —
// sanitized the same way (and for the same reason) as RichTextDisplay.tsx
// elsewhere in this app: this content is shown to everyone who can view the
// sheet, not just whoever last edited it.
function RichTextCellView({ html, onClick, interactive }: { html: string; onClick?: () => void; interactive?: boolean }) {
  const clean = html ? DOMPurify.sanitize(html) : "";
  const isEmpty = !stripHtml(clean).trim();
  return (
    <div
      onClick={onClick}
      className={`px-1 py-0.5 text-sm text-foreground font-inter whitespace-pre-wrap break-words [&_ul]:list-disc [&_ul]:list-inside [&_ol]:list-decimal [&_ol]:list-inside ${
        // A transparent border reserves the same space the active editor's
        // real border takes up, so activating a cell only swaps the border's
        // color — it never nudges the text by the border's width.
        interactive ? "min-h-[1.5rem] border border-transparent cursor-text hover:bg-surface-muted/60 rounded" : ""
      }`}
    >
      {isEmpty ? <span className="text-muted-foreground">—</span> : <span dangerouslySetInnerHTML={{ __html: clean }} />}
    </div>
  );
}

// In-place rich editor for a text cell — lets you select part of the text
// and bold/italicize/underline/resize just that selection, like Sheets.
// Deliberately uncontrolled (innerHTML is set once on mount, never re-synced
// from React state while active): re-injecting a controlled value into a
// contentEditable while the user is typing resets the cursor position every
// render — the same reason RichTextEditor.tsx uses defaultValue, not value.
function RichTextCellEditor({
  html,
  selectAllOnMount,
  onCommitHtml,
  onNavigate,
  onDeactivate,
}: {
  html: string;
  selectAllOnMount: boolean;
  onCommitHtml: (html: string) => void;
  onNavigate: (direction: NavDirection) => void;
  onDeactivate: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Removing a focused element from the DOM (which is exactly what happens
  // when Tab/Enter navigates away and this component unmounts) makes most
  // browsers fire a native blur on it as part of that removal — which would
  // re-invoke onBlur's commit() a second time via a stale closure over the
  // pre-edit `html`/version, producing a spurious second PATCH that 409s
  // even though the real edit already succeeded. This flag makes commit()
  // fire at most once per activation, whichever path gets there first.
  const committedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = DOMPurify.sanitize(html);
    // preventScroll + our own smooth scrollIntoView, rather than the
    // browser's default (instant, jump-cut) focus-scroll — matters most
    // when a freshly added row/column lands outside the current viewport.
    el.focus({ preventScroll: true });
    el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    const range = document.createRange();
    range.selectNodeContents(el);
    // Keyboard-driven arrival (Tab/Enter) selects the existing text so
    // typing overwrites it, Sheets-style; a direct click must not, so it
    // just collapses the caret to the end instead.
    if (!selectAllOnMount) range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function commit() {
    if (committedRef.current) return;
    committedRef.current = true;
    const el = ref.current;
    if (el) onCommitHtml(DOMPurify.sanitize(el.innerHTML));
  }

  function handleBlur() {
    commit();
    onDeactivate();
  }

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
      onPaste={(e) => {
        const text = e.clipboardData.getData("text/plain");
        // No text/plain flavor at all (e.g. a copied image) isn't handled
        // here — falls through to the browser's own default paste behavior,
        // same as handlePaste below does for the same case.
        if (!text) return;
        // Only a no-tab paste that actually spans multiple lines is
        // intercepted here. A tab is the one reliable signal of a genuine
        // multi-column spreadsheet block (Excel/Sheets always separate
        // columns with tabs), so that's left alone to bubble up to the
        // grid's own paste handler (see handlePaste) — same as a plain
        // single-line paste, which is left to the browser's native paste so
        // any real formatting in the clipboard's HTML (bold, a link, ...)
        // is preserved rather than flattened to plain text. It's only the
        // no-tab-but-multi-line case — e.g. a paragraph copied from a doc or
        // webpage — that handlePaste would otherwise misread as one row per
        // line, dropping the first line into this cell and spawning a new
        // row below it per remaining line.
        if (text.includes("\t") || !text.includes("\n")) return;
        e.preventDefault();
        e.stopPropagation();
        const html = splitPasteLines(text)
          .map((line) => line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"))
          .join("<br>");
        document.execCommand("insertHTML", false, html);
      }}
      onKeyDown={(e) => {
        const metaOrCtrl = e.metaKey || e.ctrlKey;
        if (metaOrCtrl && !e.shiftKey && (e.key === "b" || e.key === "i" || e.key === "u")) {
          e.preventDefault();
          document.execCommand(e.key === "b" ? "bold" : e.key === "i" ? "italic" : "underline");
          return;
        }
        if (metaOrCtrl && e.shiftKey && (e.key === "x" || e.key === "X")) {
          e.preventDefault();
          document.execCommand("strikeThrough");
          return;
        }
        // e.code (physical key), not e.key — Shift+8/7 produce "*"/"&" as
        // e.key on a standard layout, not the digit itself.
        if (metaOrCtrl && e.shiftKey && e.code === "Digit8") {
          e.preventDefault();
          document.execCommand("insertUnorderedList");
          return;
        }
        if (metaOrCtrl && e.shiftKey && e.code === "Digit7") {
          e.preventDefault();
          document.execCommand("insertOrderedList");
          return;
        }
        if (e.key === "Tab") {
          e.preventDefault();
          commit();
          onNavigate(e.shiftKey ? "left" : "right");
        } else if (e.key === "Enter" && !e.shiftKey) {
          const li = closestListItem(window.getSelection()?.anchorNode ?? null);
          if (li && ref.current?.contains(li)) {
            if (li.textContent?.trim()) {
              // Non-empty list item — let the browser's native contentEditable
              // behavior create the next <li> instead of committing/leaving
              // the cell, matching every other list editor's Enter behavior.
              return;
            }
            // An empty list item is the universal "done with this list"
            // signal (the Docs/Word double-Enter-to-exit convention) —
            // remove it (and the list itself if it was the only item) before
            // falling through to the normal commit+navigate-down below.
            const parentList = li.parentElement;
            li.remove();
            if (parentList && parentList.children.length === 0) parentList.remove();
          }
          e.preventDefault();
          commit();
          onNavigate("down");
        } else if (e.key === "Enter" && e.shiftKey) {
          e.preventDefault();
          document.execCommand("insertLineBreak");
        } else if (e.key === "Escape") {
          committedRef.current = true; // discard — a trailing blur must not commit either
          if (ref.current) ref.current.innerHTML = DOMPurify.sanitize(html);
          onDeactivate();
        }
      }}
      className="w-full min-h-[1.5rem] bg-surface-muted border border-primary rounded px-1 py-0.5 text-sm text-foreground font-inter whitespace-pre-wrap break-words focus:outline-none [&_ul]:list-disc [&_ul]:list-inside [&_ol]:list-decimal [&_ol]:list-inside"
    />
  );
}

// A cell becomes a live, focused input the moment it's "active" — clicking it
// or tabbing/entering into it from a neighbor both activate it the same way,
// so the whole grid can be filled in by keyboard alone, Sheets-style.
function EditableCell({
  column,
  value,
  editable,
  isActive,
  selectAllOnActivate,
  onActivate,
  onCommit,
  onDeactivate,
  onNavigate,
}: {
  column: SheetColumn;
  value: unknown;
  editable: boolean;
  isActive: boolean;
  selectAllOnActivate: boolean;
  onActivate: () => void;
  onCommit: (value: unknown) => void;
  onDeactivate: () => void;
  onNavigate: (direction: NavDirection) => void;
}) {
  const [draft, setDraft] = useState<string>(value == null ? "" : String(value));
  const controlRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);
  // Guards against a stray native blur (fired when Tab/Enter/Escape causes
  // this control to be removed from the DOM) re-running commit() a second
  // time via a stale closure — same pattern as RichTextCellEditor below.
  const committedRef = useRef(false);

  useEffect(() => {
    setDraft(value == null ? "" : String(value));
  }, [value]);

  useEffect(() => {
    if (isActive) committedRef.current = false;
  }, [isActive]);

  // Landing here via Tab/Enter from a neighboring cell should focus the real
  // DOM control and, for text-like inputs, select the existing value so
  // typing immediately overwrites it — matching Sheets. A direct click must
  // NOT select-all (selectAllOnActivate is false for that path), so it just
  // focuses and leaves the natural caret placement alone.
  // ("text" columns render their own rich editor below, with its own
  // mount-time focus/select handling, so this only ever applies to the
  // checkbox/select/number/date/url controls.)
  useEffect(() => {
    if (!isActive) return;
    const el = controlRef.current;
    if (!el) return;
    // preventScroll + our own smooth scrollIntoView, rather than the
    // browser's default (instant, jump-cut) focus-scroll — matters most
    // when a freshly added row/column lands outside the current viewport.
    el.focus({ preventScroll: true });
    el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
    if (selectAllOnActivate && el instanceof HTMLInputElement && el.type !== "checkbox") el.select();
  }, [isActive, selectAllOnActivate]);

  function handleNavKeyDown(e: React.KeyboardEvent, commitFn?: () => void) {
    if (e.key === "Tab") {
      e.preventDefault();
      commitFn?.();
      onNavigate(e.shiftKey ? "left" : "right");
    } else if (e.key === "Enter") {
      e.preventDefault();
      commitFn?.();
      onNavigate("down");
    } else if (e.key === "Escape") {
      committedRef.current = true;
      setDraft(value == null ? "" : String(value));
      onDeactivate();
    }
  }

  if (column.type === "text") {
    if (!editable) return <RichTextCellView html={draft} />;
    if (!isActive) return <RichTextCellView html={draft} onClick={onActivate} interactive />;
    return (
      <RichTextCellEditor
        html={draft}
        selectAllOnMount={selectAllOnActivate}
        onCommitHtml={(nextHtml) => { if (nextHtml !== draft) onCommit(nextHtml); }}
        onNavigate={onNavigate}
        onDeactivate={onDeactivate}
      />
    );
  }

  if (column.type === "boolean") {
    return (
      <input
        ref={controlRef as RefObject<HTMLInputElement>}
        type="checkbox"
        checked={!!value}
        disabled={!editable}
        onChange={(e) => { onActivate(); onCommit(e.target.checked); }}
        onFocus={onActivate}
        onBlur={onDeactivate}
        onKeyDown={handleNavKeyDown}
        className="w-4 h-4"
      />
    );
  }

  if (column.type === "select") {
    return (
      <select
        ref={controlRef as RefObject<HTMLSelectElement>}
        value={typeof value === "string" ? value : ""}
        disabled={!editable}
        onChange={(e) => { onActivate(); onCommit(e.target.value || null); }}
        onFocus={onActivate}
        onBlur={onDeactivate}
        onKeyDown={handleNavKeyDown}
        className="w-full bg-transparent text-sm text-foreground font-inter focus:outline-none disabled:opacity-70"
      >
        <option value="">—</option>
        {(column.select_options ?? []).map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }

  function commit() {
    if (committedRef.current) return;
    committedRef.current = true;
    let v: unknown = draft.trim() === "" ? null : draft;
    if (column.type === "number" && draft.trim() !== "") v = Number(draft);
    if (v !== value) onCommit(v);
  }

  function handleBlur() {
    commit();
    onDeactivate();
  }

  const displayValue = column.type === "date" ? formatSheetDate(draft) : draft;

  if (!editable) {
    return (
      <div className="px-1 py-0.5 text-sm text-foreground font-inter whitespace-pre-wrap break-words">
        {column.type === "url" && draft && isSafeUrl(draft) ? (
          <a href={draft} target="_blank" rel="noreferrer" className="text-primary hover:underline">{draft}</a>
        ) : (displayValue || <span className="text-muted-foreground">—</span>)}
      </div>
    );
  }

  if (!isActive) {
    return (
      <div
        onClick={onActivate}
        className="min-h-[1.5rem] px-1 py-0.5 border border-transparent cursor-text text-sm text-foreground font-inter whitespace-pre-wrap break-words hover:bg-surface-muted/60 rounded"
      >
        {column.type === "url" && draft && isSafeUrl(draft) ? (
          <a href={draft} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-primary hover:underline">{draft}</a>
        ) : (displayValue || <span className="text-muted-foreground">—</span>)}
      </div>
    );
  }

  return (
    <input
      ref={controlRef as RefObject<HTMLInputElement>}
      type={column.type === "number" ? "number" : column.type === "date" ? "date" : "text"}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={(e) => handleNavKeyDown(e, commit)}
      className="w-full bg-surface-muted border border-primary rounded px-1 py-0.5 text-sm text-foreground font-inter focus:outline-none"
    />
  );
}

function SheetGrid() {
  const {
    isAdmin, canEdit, canManageColumns, currentSheet, columns, rows, rowsPage, rowsTotalPages, setRowsPage,
    detailLoading, detailError, editRow, removeRow, restoreRow, addRow, pasteNewRows, triggerExportXlsx,
    addColumn, editColumn, removeColumn, reorderColumns, reorderRows, editRowHeight,
    rowConflict, clearRowConflict, acceptRowConflict, hasUnsavedChanges,
  } = useSheets();

  const handleExport = () => {
    // Export always reads the server's last-saved data — with auto-save
    // off, any not-yet-saved edit would otherwise silently end up missing
    // from the downloaded file with no indication.
    if (hasUnsavedChanges && !window.confirm(
      "You have unsaved changes that are not saved yet. The exported file will not include them — export anyway?"
    )) return;
    triggerExportXlsx();
  };

  const [sorting, setSorting] = useState<SortingState>([]);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [draggedColumnId, setDraggedColumnId] = useState<number | null>(null);
  const [draggedRowId, setDraggedRowId] = useState<number | null>(null);
  const [dragOverColumnKey, setDragOverColumnKey] = useState<string | null>(null);
  const [dragOverRowId, setDragOverRowId] = useState<number | null>(null);
  // Live preview of a row-height drag, before it's persisted on mouseup —
  // `rows` (from context) only reflects the last-committed height.
  const [resizingRow, setResizingRow] = useState<{ rowId: number; height: number } | null>(null);
  const resizingRowHeightRef = useRef(0);
  // If the grid unmounts mid-drag (e.g. the user navigates back to the
  // sheets list before releasing the mouse), this removes the still-armed
  // document listeners — otherwise they'd leak forever, and the eventual
  // mouseup would still fire editRowHeight against a sheet the user already
  // left, via a stale closure over this drag's rowId.
  const rowResizeCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => () => rowResizeCleanupRef.current?.(), []);

  const beginRowResize = useCallback((rowId: number, startHeight: number, startClientY: number) => {
    resizingRowHeightRef.current = startHeight;
    setResizingRow({ rowId, height: startHeight });

    const onMouseMove = (e: MouseEvent) => {
      const next = Math.max(MIN_ROW_HEIGHT, startHeight + (e.clientY - startClientY));
      resizingRowHeightRef.current = next;
      setResizingRow({ rowId, height: next });
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      rowResizeCleanupRef.current = null;
      setResizingRow(null);
      editRowHeight(rowId, resizingRowHeightRef.current);
    };
    rowResizeCleanupRef.current = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [editRowHeight]);

  // The backend already only ever returns active columns (list_columns filters
  // at the query level, and SheetColumnResponse has no is_active field at all),
  // so `columns` here is always the active set — no further filtering needed.
  const activeColumns = columns;

  // tanstack-table addresses columns by `column.id`, which we set to the
  // column's `key` (see columnDefs below) — resolve the currently-dragged
  // column's key once so the whole column (header + every body cell) can
  // be dimmed while dragging, not just the header.
  const draggedColumnKey = draggedColumnId !== null
    ? activeColumns.find((c) => c.id === draggedColumnId)?.key
    : undefined;

  // The single "active" cell, addressed by the row's real database id (not
  // its array position) — tanstack-table's Row.index is fixed at each row's
  // *pre-sort* (core) position and is never updated to reflect where it
  // lands after getSortedRowModel reorders things (confirmed against the
  // library's source: the sorted model shallow-copies rows via `{...row}`
  // and only reorders the array, it never rewrites `.index`). Addressing by
  // id instead of index keeps Tab/Enter navigation and paste correct
  // regardless of whether a column sort is active.
  const [activeCell, setActiveCell] = useState<{ rowId: number; col: number; selectAll: boolean } | null>(null);
  useEffect(() => { setActiveCell(null); }, [rowsPage, currentSheet?.id]);

  // moveActiveCell needs the current *displayed* (sorted) row order, which
  // only exists once `table` below is constructed — but columnDefs (used to
  // build `table`) needs a stable callback to hand each cell. A ref sidesteps
  // that ordering cycle: columnDefs closes over the ref (stable identity, no
  // need to list it as a useMemo dependency), and the ref's target is kept
  // up to date after `table` exists.
  const moveActiveCellRef = useRef<(rowId: number, colIndex: number, direction: NavDirection) => void>(() => {});

  // Renaming a column is a direct click on its header label — lifted up
  // here (rather than local state inside the header renderer) because
  // columnDefs below gets recomputed on every cell navigation, which would
  // otherwise remount the header function and wipe out any local state.
  const [renamingColumnId, setRenamingColumnId] = useState<number | null>(null);
  const [renameDraft, setRenameDraft] = useState("");

  const commitRename = useCallback(() => {
    const columnId = renamingColumnId;
    setRenamingColumnId(null);
    if (columnId == null) return;
    const col = activeColumns.find((c) => c.id === columnId);
    const trimmed = renameDraft.trim();
    if (!col || !trimmed || trimmed === col.label) return;
    editColumn(columnId, { label: trimmed });
  }, [renamingColumnId, renameDraft, activeColumns, editColumn]);

  // Switching a column's type to "select" needs at least one option before
  // the backend will accept it, so that switch is deferred until this small
  // inline (comma-separated) editor commits — same lifted-state reasoning as
  // renaming above.
  const [editingOptionsColumnId, setEditingOptionsColumnId] = useState<number | null>(null);
  const [optionsDraft, setOptionsDraft] = useState("");

  const commitOptionsEdit = useCallback(() => {
    const columnId = editingOptionsColumnId;
    setEditingOptionsColumnId(null);
    if (columnId == null) return;
    const opts = optionsDraft.split(",").map((s) => s.trim()).filter(Boolean);
    if (opts.length === 0) return; // backend requires >=1 option for a select column — bail rather than error
    editColumn(columnId, { type: "select", select_options: opts });
  }, [editingOptionsColumnId, optionsDraft, editColumn]);

  const changeColumnType = useCallback((col: SheetColumn, newType: SheetColumnType) => {
    if (newType === col.type) return;
    if (newType === "select") {
      setOptionsDraft((col.select_options ?? []).join(", "));
      setEditingOptionsColumnId(col.id);
      return;
    }
    editColumn(col.id, { type: newType, select_options: [] });
  }, [editColumn]);

  // Deleting either a row or a column confirms first, then arms an 8s undo
  // banner as a secondary safety net — a single shared slot, not one per
  // kind, so undo only ever offers to reverse whichever delete happened
  // most recently (deleting a column then a row makes the column no longer
  // undoable, matching a single "last action" mental model).
  const [pendingDelete, setPendingDelete] = useState<
    { kind: "column"; column: SheetColumn } | { kind: "row"; row: SheetRow } | null
  >(null);
  const deleteUndoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (deleteUndoTimeoutRef.current) clearTimeout(deleteUndoTimeoutRef.current); }, []);

  const armDeleteUndo = useCallback((next: { kind: "column"; column: SheetColumn } | { kind: "row"; row: SheetRow }) => {
    if (deleteUndoTimeoutRef.current) clearTimeout(deleteUndoTimeoutRef.current);
    setPendingDelete(next);
    deleteUndoTimeoutRef.current = setTimeout(() => setPendingDelete(null), 8000);
  }, []);

  // Both awaited (with a catch that surfaces the failure) rather than
  // fire-and-forget — otherwise a rejected delete (e.g. the sheet went
  // read-only, or a 404 from a race with another editor) still armed the
  // Undo banner for a delete that never actually happened, and clicking
  // Undo would then try to re-create/restore something never removed.
  const handleDeleteColumn = useCallback(async (col: SheetColumn) => {
    if (!window.confirm(`Delete column "${col.label}"? This also removes its data from every row.`)) return;
    try {
      await removeColumn(col.id);
    } catch (err: any) {
      window.alert(err?.response?.data?.detail || err?.message || "Failed to delete column");
      return;
    }
    armDeleteUndo({ kind: "column", column: col });
  }, [removeColumn, armDeleteUndo]);

  const handleDeleteRow = useCallback(async (row: SheetRow) => {
    if (!window.confirm("Delete this row?")) return;
    try {
      await removeRow(row.id);
    } catch (err: any) {
      window.alert(err?.response?.data?.detail || err?.message || "Failed to delete row");
      return;
    }
    armDeleteUndo({ kind: "row", row });
  }, [removeRow, armDeleteUndo]);

  const undoPendingDelete = useCallback(async () => {
    if (!pendingDelete) return;
    if (deleteUndoTimeoutRef.current) clearTimeout(deleteUndoTimeoutRef.current);
    setPendingDelete(null);
    try {
      if (pendingDelete.kind === "column") {
        const col = pendingDelete.column;
        await addColumn({
          key: col.key,
          label: col.label,
          type: col.type,
          select_options: col.select_options ?? undefined,
          width: col.width ?? undefined,
        });
      } else {
        await restoreRow(pendingDelete.row.id);
      }
    } catch (err: any) {
      // The banner is already gone by this point (dismissed above so a
      // double-click can't re-fire the same undo) — this is the only
      // remaining way to tell the user their undo didn't actually happen.
      window.alert(err?.response?.data?.detail || err?.message || "Failed to undo delete");
    }
  }, [pendingDelete, addColumn, restoreRow]);

  const columnDefs = useMemo<ColumnDef<SheetRow>[]>(() => {
    const dataCols: ColumnDef<SheetRow>[] = activeColumns.map((col, colIndex) => ({
      id: col.key,
      header: () => {
        if (canManageColumns && renamingColumnId === col.id) {
          return (
            <input
              autoFocus
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.target.select()}
              onBlur={commitRename}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") { e.preventDefault(); commitRename(); }
                if (e.key === "Escape") setRenamingColumnId(null);
              }}
              className="w-full bg-surface-muted border border-primary rounded px-1 py-0.5 text-xs font-medium text-foreground font-inter normal-case tracking-normal focus:outline-none"
            />
          );
        }
        if (canManageColumns && editingOptionsColumnId === col.id) {
          return (
            <input
              autoFocus
              value={optionsDraft}
              onChange={(e) => setOptionsDraft(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onBlur={commitOptionsEdit}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") { e.preventDefault(); commitOptionsEdit(); }
                if (e.key === "Escape") setEditingOptionsColumnId(null);
              }}
              placeholder="Option A, Option B, ..."
              className="w-full bg-surface-muted border border-primary rounded px-1 py-0.5 text-[10px] font-normal normal-case tracking-normal text-foreground font-inter focus:outline-none"
            />
          );
        }
        return (
          <div
            draggable={canManageColumns}
            onDragStart={(e) => {
              if (!canManageColumns) return;
              e.stopPropagation();
              e.dataTransfer.setData("text/plain", String(col.id));
              e.dataTransfer.effectAllowed = "move";
              setDraggedColumnId(col.id);
            }}
            onDragEnd={() => { setDraggedColumnId(null); setDragOverColumnKey(null); }}
            // flex-1 (rather than shrink-wrapping to the label's content
            // width) fills the rest of the header cell — including the
            // empty space to the right of a short label — so the whole
            // cell is grabbable, not just the text itself.
            className={`flex flex-col gap-0.5 py-0.5 group/header flex-1 min-w-0 ${canManageColumns ? "cursor-grab active:cursor-grabbing" : ""}`}
            title={canManageColumns ? "Drag to reorder this column" : undefined}
          >
            <div className="flex items-center gap-1.5">
              <span
                onClick={(e) => {
                  if (!canManageColumns) return;
                  e.stopPropagation();
                  setRenameDraft(col.label);
                  setRenamingColumnId(col.id);
                }}
                className={`truncate font-bold ${canManageColumns ? "cursor-text hover:text-foreground" : ""}`}
                title={canManageColumns ? "Click to rename" : undefined}
              >
                {col.label}
              </span>
              {canManageColumns && (
                <>
                  <select
                    value={col.type}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => changeColumnType(col, e.target.value as SheetColumnType)}
                    title="Column type"
                    className="bg-transparent text-[10px] font-normal normal-case tracking-normal text-muted-foreground hover:text-foreground focus:outline-none opacity-0 group-hover/header:opacity-100 focus:opacity-100 shrink-0"
                  >
                    {COLUMN_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteColumn(col); }}
                    title={`Delete column "${col.label}"`}
                    className="text-muted-foreground hover:text-destructive opacity-0 group-hover/header:opacity-100 shrink-0"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
            {canManageColumns && col.type === "select" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOptionsDraft((col.select_options ?? []).join(", "));
                  setEditingOptionsColumnId(col.id);
                }}
                className="text-[9px] font-normal normal-case tracking-normal text-muted-foreground hover:text-foreground text-left truncate"
              >
                {(col.select_options ?? []).length > 0 ? (col.select_options ?? []).join(", ") : "Set options…"}
              </button>
            )}
          </div>
        );
      },
      accessorFn: (row) => row.data?.[col.key],
      size: col.width ?? 160,
      enableSorting: col.type !== "boolean",
      // Text values may contain rich HTML markup — sort by their visible
      // text, not the raw markup (which would otherwise skew ordering, e.g.
      // "<b>Zeta</b>" sorting before "Alpha" purely because "<" < "A").
      // Only set for text columns: TanStack merges the column def OVER its defaults, so an explicit
      // `sortingFn: undefined` would clobber the default "auto" and crash every other column type's sort
      // ("sortingFn is not a function").
      ...(col.type === "text"
        ? { sortingFn: (rowA, rowB, columnId) => stripHtml(String(rowA.getValue(columnId) ?? "")).localeCompare(stripHtml(String(rowB.getValue(columnId) ?? ""))) }
        : {}),
      cell: (info) => {
        const rowId = info.row.original.id;
        const isActive = !!activeCell && activeCell.rowId === rowId && activeCell.col === colIndex;
        const selectAllOnActivate = isActive && !!activeCell?.selectAll;
        return (
          <EditableCell
            column={col}
            value={info.getValue()}
            editable={canEdit}
            isActive={isActive}
            selectAllOnActivate={selectAllOnActivate}
            onActivate={() => setActiveCell({ rowId, col: colIndex, selectAll: false })}
            onDeactivate={() =>
              // Functional form so a stale/late blur (e.g. from the DOM
              // removal a Tab/Enter-nav or a click on a different cell
              // triggers) never clobbers a cell that already activated
              // elsewhere in the meantime — it only clears if this cell is
              // still the active one.
              setActiveCell((prev) => (prev && prev.rowId === rowId && prev.col === colIndex ? null : prev))
            }
            onNavigate={(direction) => moveActiveCellRef.current(rowId, colIndex, direction)}
            onCommit={(v) => editRow(info.row.original.id, { ...info.row.original.data, [col.key]: v }, info.row.original.version)}
          />
        );
      },
    }));

    if (canEdit) {
      // Row drag-and-drop only makes sense against the sheet's own manual
      // order — while a column sort is active, the visible order no longer
      // matches `rows`' real position order, so dragging is disabled rather
      // than silently reordering something other than what's on screen.
      const canDragRows = sorting.length === 0;
      dataCols.unshift({
        id: "__row_controls",
        header: "",
        size: 64,
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          const rowId = row.id;
          return (
            // The grip + collapse cluster stays in normal flow at the top of
            // the cell (so a collapsed row's own height is never forced
            // taller); delete is positioned absolutely just below it instead
            // of beside it — real vertical separation from the collapse
            // button instead of the few px of horizontal gap this narrow
            // column otherwise leaves, without growing a collapsed row's
            // rendered height. It only fades in on hover of this control
            // cell, so it isn't a visible target at all until the user is
            // deliberately interacting with the row — and on a collapsed
            // row, briefly overlapping the row below while hovering is an
            // acceptable trade for a much lower chance of misclicking it
            // while aiming for collapse/the drag handle.
            <div className="group/rowctl relative h-full min-h-[1.25rem]">
              <div className="flex items-center gap-1">
                <div
                  draggable={canDragRows}
                  onDragStart={(e) => {
                    if (!canDragRows) return;
                    e.stopPropagation();
                    e.dataTransfer.setData("text/plain", String(rowId));
                    e.dataTransfer.effectAllowed = "move";
                    setDraggedRowId(rowId);
                  }}
                  onDragEnd={() => { setDraggedRowId(null); setDragOverRowId(null); }}
                  title={canDragRows ? "Drag to reorder this row" : "Clear sorting to reorder rows"}
                  className={`shrink-0 flex items-center justify-center ${
                    canDragRows ? "cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground" : "text-muted-foreground/70 cursor-not-allowed"
                  }`}
                >
                  <GripVertical className="w-3.5 h-3.5" />
                </div>
                {/* Toggles between auto height (grows to fit content — the
                    default) and a compact single-line height, reusing the
                    same height field the drag handle below sets — a row a
                    user dragged to some custom height shows as "collapsed"
                    too (there's something constraining it), and clicking
                    here resets it back to auto rather than only compacting
                    further. */}
                <button
                  onClick={() => editRowHeight(rowId, row.height != null ? null : MIN_ROW_HEIGHT)}
                  title={row.height != null ? "Expand row" : "Collapse row"}
                  className="shrink-0 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  {row.height != null ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
                </button>
              </div>
              <button
                onClick={() => handleDeleteRow(row)}
                className="absolute left-0 top-5 z-10 shrink-0 text-destructive hover:text-destructive/80 opacity-0 group-hover/rowctl:opacity-100 focus:opacity-100 transition-opacity"
                title="Delete row"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        },
      });
    }
    return dataCols;
  }, [
    activeColumns, canEdit, editRow, canManageColumns, activeCell, sorting.length, handleDeleteRow, editRowHeight,
    renamingColumnId, renameDraft, commitRename,
    editingOptionsColumnId, optionsDraft, commitOptionsEdit, changeColumnType, handleDeleteColumn,
  ]);

  const table = useReactTable({
    data: rows,
    columns: columnDefs,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    columnResizeMode: "onChange",
  });

  useEffect(() => {
    moveActiveCellRef.current = (rowId: number, colIndex: number, direction: NavDirection) => {
      const displayRows = table.getRowModel().rows;
      const numCols = activeColumns.length;
      if (displayRows.length === 0 || numCols === 0) return;
      let idx = displayRows.findIndex((r) => r.original.id === rowId);
      if (idx === -1) idx = 0;
      let c = colIndex;
      if (direction === "down") idx = Math.min(displayRows.length - 1, idx + 1);
      else if (direction === "up") idx = Math.max(0, idx - 1);
      else if (direction === "right") {
        c += 1;
        if (c >= numCols) { c = 0; idx = Math.min(displayRows.length - 1, idx + 1); }
      } else if (direction === "left") {
        c -= 1;
        if (c < 0) { c = numCols - 1; idx = Math.max(0, idx - 1); }
      }
      // Landing on a cell via keyboard nav selects its existing text so
      // typing immediately overwrites it, matching Sheets — but a direct
      // click (see onActivate below) must not, so that one carries selectAll.
      setActiveCell({ rowId: displayRows[idx].original.id, col: c, selectAll: true });
    };
  }, [table, activeColumns.length]);

  // columnResizeMode only updates tanstack-table's own in-memory size state —
  // it doesn't persist anywhere on its own. Column width is a structural
  // change (like type/label), so only admins can save it; save exactly once,
  // when a drag ends (columnSizingInfo.isResizingColumn flips back to false),
  // not on every pixel of movement mid-drag.
  const resizingColumnKey = table.getState().columnSizingInfo.isResizingColumn;
  const prevResizingColumnKeyRef = useRef<string | false>(false);
  useEffect(() => {
    const wasResizing = prevResizingColumnKeyRef.current;
    prevResizingColumnKeyRef.current = resizingColumnKey;
    if (!wasResizing || resizingColumnKey) return; // only fires on the true -> false transition
    if (!canManageColumns) return;
    const finalWidth = table.getState().columnSizing[wasResizing];
    const col = activeColumns.find((c) => c.key === wasResizing);
    if (col && typeof finalWidth === "number") {
      editColumn(col.id, { width: Math.round(finalWidth) });
    }
  }, [resizingColumnKey, canManageColumns, table, activeColumns, editColumn]);

  const [pasting, setPasting] = useState(false);
  const [pasteNotice, setPasteNotice] = useState<string | null>(null);

  const handlePaste = useCallback(async (e: React.ClipboardEvent<HTMLDivElement>) => {
    if (!canEdit || !activeCell) return;
    const text = e.clipboardData.getData("text/plain");
    if (!text) return;
    // A plain single value (no tabs/newlines) is left to the browser's normal
    // paste-into-the-focused-input behavior; only a real multi-cell block —
    // the kind you'd copy out of Google Sheets or Excel — gets intercepted.
    // A text-column paste with no tab but multiple lines never reaches here
    // at all: its own editor (RichTextCellEditor) already consumed it as
    // this-cell-only content — see that component's onPaste for why.
    if (!text.includes("\t") && !text.includes("\n")) return;
    e.preventDefault();

    const pasteLines = splitPasteLines(text);
    const grid = pasteLines.map((line) => line.split("\t"));
    if (!grid.length) return;

    const displayRows = table.getRowModel().rows;
    const startIdx = displayRows.findIndex((r) => r.original.id === activeCell.rowId);
    if (startIdx === -1) return; // the active row no longer exists (e.g. just deleted) — bail safely
    const numCols = activeColumns.length;
    let columnsClipped = false;

    const existingUpdates: { rowId: number; version: number; data: Record<string, unknown> }[] = [];
    const newRowsData: Record<string, unknown>[] = [];

    grid.forEach((line, i) => {
      const targetRowIndex = startIdx + i;
      const isExisting = targetRowIndex < displayRows.length;
      const baseData: Record<string, unknown> = isExisting ? { ...displayRows[targetRowIndex].original.data } : {};
      line.forEach((cellText, j) => {
        const colIndex = activeCell.col + j;
        if (colIndex >= numCols) { columnsClipped = true; return; }
        const col = activeColumns[colIndex];
        baseData[col.key] = coerceValueForColumn(col, cellText);
      });
      if (isExisting) {
        const original = displayRows[targetRowIndex].original;
        existingUpdates.push({ rowId: original.id, version: original.version, data: baseData });
      } else {
        newRowsData.push(baseData);
      }
    });

    setPasting(true);
    setPasteNotice(null);
    try {
      await Promise.all(existingUpdates.map((u) => editRow(u.rowId, u.data, u.version)));
      if (newRowsData.length > 0) await pasteNewRows(newRowsData);
      if (columnsClipped) {
        setPasteNotice("Some pasted columns beyond the sheet's last column were ignored.");
      }
    } finally {
      setPasting(false);
    }
  }, [canEdit, activeCell, table, activeColumns, editRow, pasteNewRows]);

  // The app shell's <main> only ever grows to fit its content (no bounded
  // height), so the grid's own "flex-1 overflow-auto" wrapper never actually
  // had a ceiling to overflow against — the whole document scrolled instead
  // of just the table body, taking the toolbar and column headers with it.
  // Measuring the wrapper's own top offset and capping its max-height at
  // "viewport minus that offset" gives it a real ceiling, so only its
  // content (the table) scrolls once it exceeds that cap; the sticky
  // `<th>`s below keep the column headers pinned within that. Using
  // max-height rather than a fixed height lets a small sheet still shrink
  // to fit its rows instead of stretching the box down to the viewport
  // edge with empty space below.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollAreaHeight, setScrollAreaHeight] = useState<number>();

  const recomputeScrollAreaHeight = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    // Mirrors <main>'s own bottom padding (p-5 / lg:p-8) so the scrollbar
    // doesn't sit flush against the viewport edge.
    const bottomGutter = window.innerWidth >= 1024 ? 32 : 20;
    setScrollAreaHeight(Math.max(160, window.innerHeight - top - bottomGutter));
  }, []);

  useLayoutEffect(() => {
    recomputeScrollAreaHeight();
    window.addEventListener("resize", recomputeScrollAreaHeight);
    return () => window.removeEventListener("resize", recomputeScrollAreaHeight);
  }, [recomputeScrollAreaHeight]);

  // Anything appearing above the grid (the toolbar wrapping onto a second
  // line on a narrow screen, or the row-conflict/paste/delete-undo banners)
  // shifts the wrapper's top offset without the window itself resizing.
  useLayoutEffect(() => {
    recomputeScrollAreaHeight();
  }, [recomputeScrollAreaHeight, rowConflict, pendingDelete, pasteNotice, activeColumns.length]);

  // The toolbar's format buttons act on whatever text is currently selected
  // inside the active cell's rich editor (via document.execCommand / the
  // custom font-size wrapper below) — they just need to know whether the
  // active cell is a "text" column at all, to enable/disable themselves.
  const activeColumn = activeCell ? activeColumns[activeCell.col] : undefined;
  const canFormatActiveCell = canEdit && activeColumn?.type === "text";

  if (detailLoading && rows.length === 0 && activeColumns.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (detailError) {
    return <p className="text-sm text-destructive font-inter">{detailError}</p>;
  }

  return (
    <div className="flex flex-col gap-3 h-full min-h-0">
      <div className="flex items-center gap-2 flex-wrap">
        {canEdit && (
          <>
            <button
              onClick={async () => {
                const created = await addRow();
                if (created && activeColumns.length > 0) {
                  setActiveCell({ rowId: created.id, col: 0, selectAll: true });
                }
              }}
              className="flex items-center gap-1.5 px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium rounded-xl font-inter transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Row
            </button>
            {isAdmin && (
              <button
                onClick={async () => {
                  // New columns always land at the end (backend assigns
                  // max_position + 1), so the current length is the new
                  // column's index before the reload that addColumn triggers.
                  const newColIndex = activeColumns.length;
                  const { key, label } = nextDefaultColumn(activeColumns);
                  const created = await addColumn({ key, label, type: "text" });
                  const firstRowId = table.getRowModel().rows[0]?.original.id;
                  if (created && firstRowId !== undefined) {
                    setActiveCell({ rowId: firstRowId, col: newColIndex, selectAll: true });
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-medium rounded-xl font-inter transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Column
              </button>
            )}
            <div
              className="flex items-center gap-1 border border-border rounded-xl px-1.5 py-1"
              title={canFormatActiveCell ? "Select text in the active cell, then click to format just that selection" : "Click into a text cell to format it"}
            >
              {/* onMouseDown preventDefault keeps the text selection inside the
                  active cell's rich editor alive — otherwise clicking a toolbar
                  button would steal focus first and clear it before onClick fires. */}
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => document.execCommand("bold")}
                disabled={!canFormatActiveCell}
                title="Bold selection (Ctrl/Cmd+B)"
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Bold className="w-3.5 h-3.5" />
              </button>
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => document.execCommand("italic")}
                disabled={!canFormatActiveCell}
                title="Italicize selection (Ctrl/Cmd+I)"
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Italic className="w-3.5 h-3.5" />
              </button>
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => document.execCommand("underline")}
                disabled={!canFormatActiveCell}
                title="Underline selection (Ctrl/Cmd+U)"
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Underline className="w-3.5 h-3.5" />
              </button>
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => document.execCommand("strikeThrough")}
                disabled={!canFormatActiveCell}
                title="Strikethrough selection (Ctrl/Cmd+Shift+X)"
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <Strikethrough className="w-3.5 h-3.5" />
              </button>
              <div className="w-px h-4 bg-border mx-0.5" />
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => document.execCommand("insertUnorderedList")}
                disabled={!canFormatActiveCell}
                title="Bulleted list (Ctrl/Cmd+Shift+8)"
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <List className="w-3.5 h-3.5" />
              </button>
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => document.execCommand("insertOrderedList")}
                disabled={!canFormatActiveCell}
                title="Numbered list (Ctrl/Cmd+Shift+7)"
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ListOrdered className="w-3.5 h-3.5" />
              </button>
              <div className="w-px h-4 bg-border mx-0.5" />
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyFontSizeToSelection("sm")}
                disabled={!canFormatActiveCell}
                title="Small size for selection"
                className="px-1.5 py-1 rounded-lg text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                S
              </button>
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyFontSizeToSelection("base")}
                disabled={!canFormatActiveCell}
                title="Normal size for selection"
                className="px-1.5 py-1 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                M
              </button>
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applyFontSizeToSelection("lg")}
                disabled={!canFormatActiveCell}
                title="Large size for selection"
                className="px-1.5 py-1 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                L
              </button>
              <div className="w-px h-4 bg-border mx-0.5" />
              <ColorSwatchButton
                icon={<Baseline className="w-3.5 h-3.5" />}
                onPick={(value) => applyColorToSelection("color", value)}
                disabled={!canFormatActiveCell}
                title="Text color for selection"
                resetLabel="Reset to default"
                resetValue="hsl(var(--foreground))"
              />
              <ColorSwatchButton
                icon={<Highlighter className="w-3.5 h-3.5" />}
                onPick={(value) => applyColorToSelection("backgroundColor", value)}
                disabled={!canFormatActiveCell}
                title="Highlight color for selection"
                resetLabel="Remove highlight"
                resetValue="transparent"
              />
            </div>
          </>
        )}
        <div className="flex items-center gap-2 ml-auto">
          {canEdit && (
            <button
              onClick={() => setShowCsvImport(true)}
              className="flex items-center gap-1.5 px-3 py-2 border border-border hover:border-muted-foreground text-muted-foreground hover:text-foreground text-xs font-medium rounded-xl font-inter transition-colors"
            >
              <UploadCloud className="w-3.5 h-3.5" />
              Import
            </button>
          )}
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 border border-border hover:border-muted-foreground text-muted-foreground hover:text-foreground text-xs font-medium rounded-xl font-inter transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            Export
          </button>
        </div>
      </div>

      {rowConflict && (
        <div className="flex items-center justify-between gap-3 text-xs text-amber-400 font-inter bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
          <span className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            This row changed since it was loaded — your edit wasn't saved.
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={acceptRowConflict} className="underline hover:text-amber-300">Reload row</button>
            <button onClick={clearRowConflict} className="text-amber-400/70 hover:text-amber-300">Dismiss</button>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div className="flex items-center justify-between gap-3 text-xs text-amber-400 font-inter bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
          <span className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            {pendingDelete.kind === "column" ? `Deleted column "${pendingDelete.column.label}".` : "Deleted row."}
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={undoPendingDelete} className="underline hover:text-amber-300">Undo</button>
            <button onClick={() => setPendingDelete(null)} className="text-amber-400/70 hover:text-amber-300">Dismiss</button>
          </div>
        </div>
      )}

      {pasteNotice && (
        <div className="flex items-center justify-between gap-3 text-xs text-amber-400 font-inter bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
          <span className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            {pasteNotice}
          </span>
          <button onClick={() => setPasteNotice(null)} className="text-amber-400/70 hover:text-amber-300 shrink-0">Dismiss</button>
        </div>
      )}

      {activeColumns.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground font-inter text-sm">
          No columns yet.{canManageColumns ? " Add one to start filling this sheet in." : ""}
        </div>
      ) : (
        <div
          ref={scrollContainerRef}
          onPaste={handlePaste}
          style={scrollAreaHeight ? { maxHeight: scrollAreaHeight } : undefined}
          // pb-3: room for the last row's delete button (positioned below the
          // collapse button, see __row_controls above) to render on hover
          // without being clipped by this container's own overflow-auto.
          className={`overflow-auto rounded-2xl border border-border bg-surface/50 pb-3 ${pasting ? "opacity-60 pointer-events-none" : ""}`}
        >
          <table className="text-sm font-inter table-fixed" style={{ width: table.getTotalSize() }}>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  {hg.headers.map((header) => {
                    const isDraggedCol = draggedColumnId !== null && header.column.id === draggedColumnKey;
                    return (
                    <th
                      key={header.id}
                      style={{ width: header.getSize(), position: "sticky", top: 0, opacity: isDraggedCol ? 0.4 : undefined }}
                      className={`z-10 px-3 py-2 font-medium select-none ${
                        // A single background/ring class, not stacked ones — sticky
                        // headers need an opaque bg-surface fallback so scrolling rows
                        // don't show through, but stacking it alongside the drag
                        // highlight classes would let bg-surface silently win the
                        // cascade (it's declared later in the compiled stylesheet
                        // than bg-primary/10, regardless of className order) and mask
                        // the drop-target highlight during column drag-and-drop.
                        isDraggedCol
                          ? "bg-surface-muted"
                          : dragOverColumnKey === header.column.id
                          ? "bg-primary/10 ring-1 ring-inset ring-primary/40"
                          : "bg-surface"
                      }`}
                      onDragOver={(e) => {
                        if (draggedColumnId === null) return;
                        const targetCol = activeColumns.find((c) => c.key === header.column.id);
                        if (!targetCol) return;
                        e.preventDefault();
                        if (dragOverColumnKey !== header.column.id) setDragOverColumnKey(header.column.id);
                      }}
                      onDragLeave={() => {
                        if (dragOverColumnKey === header.column.id) setDragOverColumnKey(null);
                      }}
                      onDrop={(e) => {
                        if (draggedColumnId === null) return;
                        const targetCol = activeColumns.find((c) => c.key === header.column.id);
                        if (!targetCol) return;
                        e.preventDefault();
                        const fromIdx = activeColumns.findIndex((c) => c.id === draggedColumnId);
                        const toIdx = activeColumns.findIndex((c) => c.id === targetCol.id);
                        setDraggedColumnId(null);
                        setDragOverColumnKey(null);
                        if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
                        const reordered = [...activeColumns];
                        const [moved] = reordered.splice(fromIdx, 1);
                        reordered.splice(toIdx, 0, moved);
                        reorderColumns(reordered.map((c) => c.id));
                      }}
                    >
                      <div
                        className={`flex items-center gap-1 ${header.column.getCanSort() ? "cursor-pointer" : ""}`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === "asc" && <ChevronUp className="w-3 h-3" />}
                        {header.column.getIsSorted() === "desc" && <ChevronDown className="w-3 h-3" />}
                      </div>
                      {header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={`absolute right-0 top-0 h-full w-1.5 cursor-col-resize bg-border/70 hover:bg-primary transition-colors ${
                            header.column.getIsResizing() ? "bg-primary" : ""
                          }`}
                        />
                      )}
                    </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columnDefs.length} className="px-4 py-12 text-center text-muted-foreground">
                    No rows yet.
                  </td>
                </tr>
              )}
              {table.getRowModel().rows.map((row) => {
                const rowId = row.original.id;
                // Reordering reads/writes `rows` (the context's raw,
                // position-ordered array) rather than the table's sorted
                // row model — dragging is disabled whenever a sort is
                // active (see canDragRows above), so the two always agree
                // on order while a drag is actually possible.
                const canDropOnRow = sorting.length === 0 && draggedRowId !== null;
                const isDraggedRow = draggedRowId === rowId;
                return (
                  <tr
                    key={row.id}
                    style={{ opacity: isDraggedRow ? 0.4 : undefined }}
                    onDragOver={(e) => {
                      if (!canDropOnRow) return;
                      e.preventDefault();
                      if (dragOverRowId !== rowId) setDragOverRowId(rowId);
                    }}
                    onDragLeave={() => { if (dragOverRowId === rowId) setDragOverRowId(null); }}
                    onDrop={(e) => {
                      if (!canDropOnRow) return;
                      e.preventDefault();
                      const fromIdx = rows.findIndex((r) => r.id === draggedRowId);
                      const toIdx = rows.findIndex((r) => r.id === rowId);
                      setDraggedRowId(null);
                      setDragOverRowId(null);
                      if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;
                      const reordered = [...rows];
                      const [moved] = reordered.splice(fromIdx, 1);
                      reordered.splice(toIdx, 0, moved);
                      reorderRows(reordered.map((r) => r.id));
                    }}
                    className={`border-b border-border/60 last:border-0 hover:bg-surface-muted/30 ${
                      isDraggedRow ? "bg-surface-muted" : ""
                    } ${
                      dragOverRowId === rowId && !isDraggedRow ? "bg-primary/10 ring-1 ring-inset ring-primary/40" : ""
                    }`}
                  >
                    {(() => {
                      // null = auto height (grows to fit content, the
                      // original/default behavior) — only a row a user has
                      // actually dragged gets a fixed height with scrollable
                      // overflow, so untouched rows never lose their normal
                      // grow-to-fit-content sizing.
                      const rowHeight = resizingRow?.rowId === rowId ? resizingRow.height : row.original.height;
                      return row.getVisibleCells().map((cell) => {
                        const isDraggedCol = cell.column.id === draggedColumnKey;
                        return (
                          <td
                            key={cell.id}
                            style={{ width: cell.column.getSize(), opacity: isDraggedCol ? 0.4 : undefined }}
                            className={`${cell.column.id === "__row_controls" ? "relative px-1.5" : "px-3"} py-1.5 align-top ${isDraggedCol ? "bg-surface-muted" : ""}`}
                          >
                            {rowHeight != null ? (
                              <div style={{ height: rowHeight, overflowY: "auto" }}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </div>
                            ) : (
                              flexRender(cell.column.columnDef.cell, cell.getContext())
                            )}
                            {cell.column.id === "__row_controls" && (
                              // Bottom-edge handle, mirroring the column resize handle on each <th>'s right edge.
                              // Anchored to the <td> (not a child of the cell content) so it sits on the row's real
                              // bottom edge even for an auto-height row — a percentage height inside an auto-height
                              // cell resolves to nothing, which left the handle ~20px from the TOP of a tall row.
                              // Plain mouse events (HTML5 DnD is for moving, not resizing); the row's current
                              // rendered height is measured on mousedown so an auto-height row doesn't jump.
                              <div
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const trEl = (e.currentTarget as HTMLElement).closest("tr");
                                  const startHeight = trEl ? trEl.getBoundingClientRect().height : MIN_ROW_HEIGHT;
                                  beginRowResize(rowId, startHeight, e.clientY);
                                }}
                                title="Drag to resize this row"
                                className="absolute left-0 right-0 bottom-0 h-2 cursor-row-resize bg-border/60 hover:bg-primary transition-colors"
                              />
                            )}
                          </td>
                        );
                      });
                    })()}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rowsTotalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm font-inter">
          <button
            onClick={() => setRowsPage(Math.max(1, rowsPage - 1))}
            disabled={rowsPage <= 1}
            className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          <span className="text-muted-foreground">Page {rowsPage} of {rowsTotalPages}</span>
          <button
            onClick={() => setRowsPage(Math.min(rowsTotalPages, rowsPage + 1))}
            disabled={rowsPage >= rowsTotalPages}
            className="p-2 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {showCsvImport && <CsvImportModal onClose={() => setShowCsvImport(false)} />}
    </div>
  );
}

function formatSavedAt(date: Date): string {
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);

  let dayLabel: string;
  if (diffDays === 0) dayLabel = "Today";
  else if (diffDays === 1) dayLabel = "Yesterday";
  else {
    dayLabel = date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  }

  const timeLabel = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
  return `${dayLabel} at ${timeLabel}`;
}

function SaveStatusIndicator() {
  const { saveStatus, saveError, lastSavedAt } = useSheets();

  if (saveStatus === "idle") return null;

  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-inter shrink-0">
      {saveStatus === "saving" && (
        <>
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Saving…
        </>
      )}
      {saveStatus === "saved" && lastSavedAt && (
        <>
          <Check className="w-3.5 h-3.5 text-green-500" />
          Last saved {formatSavedAt(lastSavedAt)}
        </>
      )}
      {saveStatus === "error" && (
        <span className="flex items-center gap-1.5 text-destructive" title={saveError ?? undefined}>
          <AlertCircle className="w-3.5 h-3.5" />
          {saveError ? `Save failed: ${saveError}` : "Save failed"}
        </span>
      )}
    </span>
  );
}

// Auto-save is per-sheet and toggleable by anyone with edit access (not just
// admin) — a view-only viewer still sees the current state, just can't flip it.
function AutoSaveToggle({ canEdit }: { canEdit: boolean }) {
  const { currentSheet, toggleAutoSave } = useSheets();
  // Guards against a rapid double-click firing two overlapping toggle
  // requests — without this, out-of-order responses could leave the
  // displayed state inconsistent with what the server actually persisted.
  const [isToggling, setIsToggling] = useState(false);
  // This component instance persists across a sheet-id route change (same
  // <SheetsProvider>/route element, just a new :id) — without this, toggling
  // on sheet A and navigating to sheet B before that PATCH resolves would
  // leave sheet B's switch spuriously disabled until A's stale request
  // finally settles.
  useEffect(() => { setIsToggling(false); }, [currentSheet?.id]);
  if (!currentSheet) return null;
  const enabled = currentSheet.auto_save_enabled;
  const disabled = !canEdit || isToggling;

  const handleClick = async () => {
    if (disabled) return;
    setIsToggling(true);
    try {
      await toggleAutoSave(!enabled);
    } catch (err: any) {
      window.alert(err?.response?.data?.detail || err?.message || "Failed to update auto-save setting");
    } finally {
      setIsToggling(false);
    }
  };

  // "View only" is wrong for an admin whose canEdit is false purely because
  // the sheet is read-only-locked, not because they lack edit access.
  const disabledTitle = currentSheet.is_readonly ? "Auto-save (sheet is read-only)" : "Auto-save (view only)";

  return (
    <div className="flex items-center gap-1.5 shrink-0" title={canEdit ? "Toggle auto-save" : disabledTitle}>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
          enabled ? "bg-primary" : "bg-border"
        } ${disabled ? "cursor-default" : "cursor-pointer"} ${!canEdit ? "opacity-70" : ""}`}
        role="switch"
        aria-checked={enabled}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
            enabled ? "translate-x-4" : "translate-x-0"
          }`}
        />
      </button>
      <span className="text-xs text-muted-foreground font-inter">Auto-save</span>
    </div>
  );
}

// Admin-only safety lock, unlike AutoSaveToggle above — no one else ever
// sees this control, matching its purpose (protecting the admin from their
// own accidental edits). SheetsContext.canEdit already folds is_readonly
// into its check, so every cell/toolbar/row/column control locks itself
// automatically once this is on; this component only needs to render the
// switch itself.
function ReadonlyToggle() {
  const { isAdmin, currentSheet, toggleReadonly } = useSheets();
  // Same double-click guard + cross-sheet-navigation reset as AutoSaveToggle.
  const [isToggling, setIsToggling] = useState(false);
  useEffect(() => { setIsToggling(false); }, [currentSheet?.id]);
  if (!isAdmin || !currentSheet) return null;
  const enabled = currentSheet.is_readonly;

  const handleClick = async () => {
    if (isToggling) return;
    setIsToggling(true);
    try {
      await toggleReadonly(!enabled);
    } catch (err: any) {
      window.alert(err?.response?.data?.detail || err?.message || "Failed to update read-only setting");
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div
      className="flex items-center gap-1.5 shrink-0"
      title={enabled ? "Sheet is read-only — click to allow editing again" : "Make this sheet read-only to prevent accidental edits"}
    >
      {enabled ? <Lock className="w-3.5 h-3.5 text-amber-500" /> : <Unlock className="w-3.5 h-3.5 text-muted-foreground" />}
      <button
        type="button"
        onClick={handleClick}
        disabled={isToggling}
        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
          enabled ? "bg-amber-500" : "bg-border"
        } ${isToggling ? "cursor-default" : "cursor-pointer"}`}
        role="switch"
        aria-checked={enabled}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
            enabled ? "translate-x-4" : "translate-x-0"
          }`}
        />
      </button>
      <span className="text-xs text-muted-foreground font-inter">Read-only</span>
    </div>
  );
}

// Non-admin counterpart to ReadonlyToggle above — an employee with edit
// share access has no toggle to look at, so without this a sheet going
// read-only would just look like their edit access silently vanished.
function ReadonlyBadge() {
  const { isAdmin, currentSheet } = useSheets();
  if (isAdmin || !currentSheet?.is_readonly) return null;
  return (
    <span
      className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 text-xs font-inter shrink-0"
      title="This sheet has been set to read-only by an admin"
    >
      <Lock className="w-3 h-3" />
      Read-only
    </span>
  );
}

// Shown alongside SaveStatusIndicator (not instead of it — see the render
// site) whenever auto-save is off for this sheet: cell edits sit in
// dirtyRowIds (see SheetsContext) until Save is clicked, while
// SaveStatusIndicator still separately covers structural mutations, which
// always save immediately regardless of this flag.
function ManualSaveControl({ canEdit }: { canEdit: boolean }) {
  const { hasUnsavedChanges, saveNow, manualSaveStatus, manualSaveError } = useSheets();
  const saving = manualSaveStatus === "saving";
  const failed = manualSaveStatus === "error";

  return (
    <span className="flex items-center gap-2 text-xs font-inter shrink-0">
      <span
        className={failed ? "text-destructive" : "text-muted-foreground"}
        title={failed && manualSaveError ? manualSaveError : undefined}
      >
        {failed ? (manualSaveError ? `Save failed: ${manualSaveError}` : "Save failed") : hasUnsavedChanges ? "Unsaved changes" : "All changes saved"}
      </span>
      {canEdit && (
        <button
          type="button"
          onClick={() => saveNow()}
          disabled={!hasUnsavedChanges || saving}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border text-foreground hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : failed ? <AlertCircle className="w-3.5 h-3.5 text-destructive" /> : <Save className="w-3.5 h-3.5" />}
          Save
        </button>
      )}
    </span>
  );
}

function SheetDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, canEdit, currentSheet, hasUnsavedChanges, loadSheetDetail } = useSheets();
  const [showShare, setShowShare] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  useEffect(() => {
    if (id) loadSheetDetail(Number(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const basePath = isAdmin ? "/admin/sheets" : "/employee/sheets";

  const handleBack = () => {
    // Same data-loss risk the beforeunload guard covers (auto-save off,
    // edits only held in SheetsContext's in-memory dirtyRowIds/rows), but
    // for in-app navigation — beforeunload never fires for this.
    if (hasUnsavedChanges && !window.confirm("You have unsaved changes. Leave without saving?")) return;
    navigate(basePath);
  };

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      <div className="flex items-center gap-3">
        <button
          onClick={handleBack}
          className="p-2 -ml-2 text-muted-foreground hover:text-foreground"
          aria-label="Back to sheets"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-foreground font-inter truncate">{currentSheet?.name ?? "Sheet"}</h1>
            {isAdmin && currentSheet && (
              <>
                <button
                  onClick={() => setShowShare(true)}
                  className="p-2 border border-border hover:border-muted-foreground text-muted-foreground hover:text-foreground rounded-lg transition-colors shrink-0"
                  title="Share sheet"
                  aria-label="Share sheet"
                >
                  <Share2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowVersionHistory(true)}
                  className="p-2 border border-border hover:border-muted-foreground text-muted-foreground hover:text-foreground rounded-lg transition-colors shrink-0"
                  title="Version history"
                  aria-label="Version history"
                >
                  <History className="w-4 h-4" />
                </button>
              </>
            )}
            {currentSheet && <ReadonlyToggle />}
            {currentSheet && <ReadonlyBadge />}
            {currentSheet && <AutoSaveToggle canEdit={canEdit} />}
            {/* Structural mutations (row/column add/delete/resize/reorder) always
                save immediately regardless of the auto-save flag, so this indicator
                stays mounted unconditionally — it renders null while idle, and is
                the only feedback (including errors) for those actions when
                auto-save is off and ManualSaveControl is showing instead. */}
            {currentSheet && <SaveStatusIndicator />}
            {currentSheet && !currentSheet.auto_save_enabled && <ManualSaveControl canEdit={canEdit} />}
          </div>
          {currentSheet?.description && (
            <p className="text-sm text-muted-foreground font-inter mt-0.5">{currentSheet.description}</p>
          )}
        </div>
      </div>
      <SheetGrid />
      {showShare && <ShareSheetModal onClose={() => setShowShare(false)} />}
      {showVersionHistory && <VersionHistoryModal onClose={() => setShowVersionHistory(false)} />}
    </div>
  );
}

export default function SheetDetailPage() {
  return (
    <SheetsProvider>
      <SheetDetailContent />
    </SheetsProvider>
  );
}
